import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { Button } from '@studykik/ui-library';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PaymentBottomButtonHint,
  PaymentNoPaymentSubmissions,
  PaymentPageWrapper,
  TransactionsWrapper,
} from './styles';
import { PaymentBottomButtonWrapper } from '../layout/styles';
import { environment } from '../../environments/environment';
import {
  BucketedReward,
  PaymentSubmission,
} from '../../models/paymentSubmissions';
import { PaymentSubmissionsTable } from './payment-submissions-table';
import { usePaymentSubmissionsContext } from '../../contexts/payment-submissions-context';
import { useAuth } from '../../contexts';
import { useLoadingSpinnerContext } from '../../contexts/loading-spinner-context';
import { usePaymentConfig } from '../hooks/payment-config-hook';
import { usePatientContext } from '../../contexts/patient-context';
import { ClaimSummary } from '../claim-summary';
import { ReclaimFundsTable } from './reclaim-rewards-table';

const PaymentPageComponent: FC<PropsWithChildren> = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const onClickSubmitPaymentRequest = () => {
    navigate('./submission');
  };

  const { patient } = usePatientContext();
  const {
    data: { paymentAllowPatientSubmit, protocolRewardConfig },
    fetchAllowPatientSubmit,
    fetchProtocolRewardConfig,
  } = usePaymentConfig();

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<PaymentSubmission[]>([]);
  const [reclaimRewardsData, setReclaimRewards] = useState<BucketedReward[]>(
    []
  );

  const { getAll, getAllReclaimRewards } = usePaymentSubmissionsContext();

  const refetchData = async () => {
    const [paymentSubmissions, reclaimRewards] = await Promise.all([
      getAll(user?.['https://studykik.com/studykik_id'] as string),
      getAllReclaimRewards(),
    ]);

    setReclaimRewards(reclaimRewards);
    setData(paymentSubmissions);
  };

  const fetchData = async () => {
    setIsLoading(true);

    const [paymentSubmissions, reclaimRewards] = await Promise.all([
      getAll(user?.['https://studykik.com/studykik_id'] as string),
      getAllReclaimRewards(),
    ]);

    setReclaimRewards(reclaimRewards);
    setData(paymentSubmissions);

    setIsLoading(false);
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (patient?.protocolId) {
      fetchAllowPatientSubmit(patient?.protocolId);
      fetchProtocolRewardConfig(patient.protocolId);
    }
  }, [patient]);

  const { setShowingLoadingSpinner } = useLoadingSpinnerContext();

  useEffect(() => {
    setShowingLoadingSpinner('fetchRequests', isLoading);
  }, [isLoading]);

  useEffect(() => {
    window.addEventListener('focus', refetchData);
    return () => {
      window.removeEventListener('focus', refetchData);
    };
  }, []);

  return (
    <PaymentPageWrapper>
      {!isLoading &&
        patient?.id &&
        protocolRewardConfig?.requestProcessorSyncOnUserAction === false && (
          <ClaimSummary
            paymentSubmissions={data}
            patientId={patient.id}
            fetchData={fetchData}
          />
        )}
      <TransactionsWrapper>
        {isLoading || reclaimRewardsData.length === 0 ? (
          <></>
        ) : (
          <>
            <Box>
              <Typography
                component="p"
                sx={{ margin: '16px 0', textAlign: 'center' }}
              >
                <b>{t('Reclaim Funds')}</b>
              </Typography>
            </Box>
            <ReclaimFundsTable reclaimRewards={reclaimRewardsData} />
          </>
        )}
        <Box>
          <Typography
            component="p"
            sx={{ margin: '16px 0', textAlign: 'center' }}
          >
            <b>{t('Payment Activity')}</b>
          </Typography>
        </Box>
        {!isLoading && (!data || data.length === 0) ? (
          <PaymentNoPaymentSubmissions>
            {t('You have no past payment submissions')}
          </PaymentNoPaymentSubmissions>
        ) : (
          <PaymentSubmissionsTable paymentSubmissions={data} />
        )}
      </TransactionsWrapper>
      {paymentAllowPatientSubmit?.allowPatientSubmitRequest !== false && (
        <PaymentBottomButtonWrapper>
          {!data || (data && data.length === 0) ? (
            <PaymentBottomButtonHint>
              <img
                src={`${environment.cdnUrl}${t(
                  '/images/payment-request-here.svg'
                )}`}
                alt=""
              />
            </PaymentBottomButtonHint>
          ) : null}
          <Button
            onClick={onClickSubmitPaymentRequest}
            data-testid="request-payment-button"
          >
            {t('Request Payment')}
          </Button>
        </PaymentBottomButtonWrapper>
      )}
    </PaymentPageWrapper>
  );
};

export default PaymentPageComponent;
